import {
  CloseOutlined,
  DownloadOutlined,
  EmojiEventsOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import useToast from "../../../hooks/useToast";
import {
  evaluation_refonte_fiche_evaluation_service,
  evaluation_refonte_reponse_service,
  evaluation_refonte_saison_service,
} from "../../../services/PersonalEvaluationRefonteService";

export const REQUEST_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  EMPTY: "empty",
  ERROR: "error",
};

const DEFAULT_ERROR_MESSAGE = "Impossible de charger l'aperçu de l'évaluation.";

const parseEvaluationDate = (value) => {
  const normalizedValue =
    typeof value === "string" ? value.replace(" ", "T") : value;
  const timestamp = Date.parse(normalizedValue || "");
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const normalizeFinalizedEvaluators = (evaluators = []) => {
  const latestByEvaluator = evaluators.reduce((accumulator, evaluator) => {
    const evaluatorId = evaluator?.user_id;
    if (!evaluatorId || !evaluator?.CREATION_DATE) return accumulator;

    const key = String(evaluatorId);
    const current = accumulator.get(key);
    if (
      !current ||
      parseEvaluationDate(evaluator.CREATION_DATE) >
        parseEvaluationDate(current.CREATION_DATE)
    ) {
      accumulator.set(key, evaluator);
    }

    return accumulator;
  }, new Map());

  return Array.from(latestByEvaluator.values()).sort((first, second) => {
    const dateDifference =
      parseEvaluationDate(second.CREATION_DATE) -
      parseEvaluationDate(first.CREATION_DATE);
    if (dateDifference !== 0) return dateDifference;

    return String(second.user_id).localeCompare(String(first.user_id), "fr", {
      numeric: true,
    });
  });
};

export const selectLatestFinalizedEvaluator = (evaluators = []) =>
  normalizeFinalizedEvaluators(evaluators)[0] || null;

const getEvaluatorName = (evaluator) => {
  const name = [evaluator?.last_name, evaluator?.first_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    name || evaluator?.FULL_NAME || evaluator?.full_name || "Non renseigné"
  );
};

export const formatEvaluationDate = (value) => {
  if (!value) return "Non renseignée";
  const normalizedValue =
    typeof value === "string" ? value.replace(" ", "T") : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const isAutoEvaluation = (evaluator, candidateId) =>
  evaluator?.user_id != null &&
  candidateId != null &&
  String(evaluator.user_id) === String(candidateId);

export const getEvaluationOptionLabel = (evaluator, candidateId) => {
  if (!evaluator) return "";
  const autoEvaluationLabel = isAutoEvaluation(evaluator, candidateId)
    ? " (auto-évaluation)"
    : "";
  return `${getEvaluatorName(evaluator)}${autoEvaluationLabel}`;
};

const getNoteClassification = (score, maximum) => {
  if (Number(maximum) <= 0) return "";
  const level = Math.ceil((Number(score) / Number(maximum)) * 5);
  if (level <= 0) return "";
  if (level === 1) return "Faible";
  if (level === 2) return "Non satisfaisant";
  if (level === 3) return "Satisfaisant";
  if (level === 4) return "Supérieur";
  return "Exceptionnel";
};

const getClassificationColors = (classification) => {
  const normalizedClassification = String(classification || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalizedClassification.includes("faible")) {
    return { color: "#b42318", background: "#fff5f4", border: "#fecdca" };
  }
  if (normalizedClassification.includes("non satisfaisant")) {
    return { color: "#b54708", background: "#fffaeb", border: "#fedf89" };
  }
  if (normalizedClassification.includes("satisfaisant")) {
    return { color: "#067647", background: "#f3fcf7", border: "#abefc6" };
  }
  if (normalizedClassification.includes("superieur")) {
    return { color: "#175cd3", background: "#f5f8ff", border: "#b2ccff" };
  }
  if (normalizedClassification.includes("exceptionnel")) {
    return { color: "#6941c6", background: "#faf8ff", border: "#d9d6fe" };
  }
  return { color: "#475467", background: "#f8fafc", border: "#d0d5dd" };
};

const getDisplayResponse = (detail) => detail.REPONSE ?? detail.REP_CA ?? "N/A";

const buildCategoryGroups = (details) =>
  details.reduce((groups, detail) => {
    const categoryName = detail.CATEGORY_NAME || "Sans catégorie";
    let group = groups.find((item) => item.categoryName === categoryName);
    if (!group) {
      group = {
        categoryName,
        categoryOrder: Number(detail.ORDRE),
        details: [],
      };
      groups.push(group);
    }
    group.details.push(detail);
    return groups;
  }, []);

const PREVIEW_CARD_SX = {
  borderRadius: 2,
  borderColor: "#e2e8f0",
  boxShadow: "0 3px 14px rgba(15, 23, 42, 0.06)",
};
const IdentificationItem = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
    >
      {value || "Non renseigné"}
    </Typography>
  </Grid>
);

const EvaluationPreviewDrawer = ({ open, evaluationRow, onClose }) => {
  const { showToast, Toast } = useToast();
  const [status, setStatus] = useState(REQUEST_STATUS.IDLE);
  const [evaluators, setEvaluators] = useState([]);
  const [evaluationDetails, setEvaluationDetails] = useState([]);
  const [selectedEvaluator, setSelectedEvaluator] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [listRetryKey, setListRetryKey] = useState(0);
  const [detailsRetryKey, setDetailsRetryKey] = useState(0);

  const candidateId = Number(evaluationRow?.candidat_id);
  const seasonId = Number(evaluationRow?.ID_SAISON);

  useEffect(() => {
    if (!open) {
      setStatus(REQUEST_STATUS.IDLE);
      setEvaluators([]);
      setEvaluationDetails([]);
      setSelectedEvaluator(null);
      setErrorMessage("");
      setIsDownloading(false);
      return undefined;
    }

    let cancelled = false;

    const loadEvaluators = async () => {
      setStatus(REQUEST_STATUS.LOADING);
      setEvaluators([]);
      setEvaluationDetails([]);
      setSelectedEvaluator(null);
      setErrorMessage("");

      if (candidateId <= 0 || seasonId <= 0) {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setStatus(REQUEST_STATUS.ERROR);
        return;
      }

      try {
        const response =
          await evaluation_refonte_reponse_service.getEvaluateurByIdCandidatAndIdSession(
            candidateId,
            seasonId,
          );
        if (cancelled) return;
        if (!Array.isArray(response?.data)) {
          throw new Error(response?.message || DEFAULT_ERROR_MESSAGE);
        }

        const finalizedEvaluators = normalizeFinalizedEvaluators(response.data);
        setEvaluators(finalizedEvaluators);
        if (finalizedEvaluators.length === 0) {
          setStatus(REQUEST_STATUS.EMPTY);
          return;
        }

        setSelectedEvaluator(finalizedEvaluators[0]);
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(error?.message || DEFAULT_ERROR_MESSAGE);
        setStatus(REQUEST_STATUS.ERROR);
      }
    };

    loadEvaluators();
    return () => {
      cancelled = true;
    };
  }, [candidateId, listRetryKey, open, seasonId]);

  useEffect(() => {
    if (!open || !selectedEvaluator?.user_id) return undefined;

    let cancelled = false;

    const loadEvaluationDetails = async () => {
      setStatus(REQUEST_STATUS.LOADING);
      setEvaluationDetails([]);
      setErrorMessage("");

      try {
        const response =
          await evaluation_refonte_fiche_evaluation_service.getEvaluationPreviewDetails(
            candidateId,
            selectedEvaluator.user_id,
            seasonId,
          );
        if (cancelled) return;
        if (response?.success === false) {
          throw new Error(response.message || DEFAULT_ERROR_MESSAGE);
        }
        if (!Array.isArray(response?.data)) {
          throw new Error(DEFAULT_ERROR_MESSAGE);
        }

        setEvaluationDetails(response.data);
        setStatus(
          response.data.length > 0
            ? REQUEST_STATUS.SUCCESS
            : REQUEST_STATUS.EMPTY,
        );
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(error?.message || DEFAULT_ERROR_MESSAGE);
        setStatus(REQUEST_STATUS.ERROR);
      }
    };

    loadEvaluationDetails();
    return () => {
      cancelled = true;
    };
  }, [candidateId, detailsRetryKey, open, seasonId, selectedEvaluator]);
  const categoryGroups = useMemo(
    () => buildCategoryGroups(evaluationDetails),
    [evaluationDetails],
  );

  const totals = useMemo(
    () =>
      evaluationDetails.reduce(
        (result, detail) => {
          const isNote = String(detail.TYPE_NAME || "")
            .toLowerCase()
            .includes("note");
          if (!isNote) return result;
          return {
            score: result.score + Number(detail.NOTE ?? detail.NOTE_CA ?? 0),
            maximum: result.maximum + Number(detail.NOTE_MAX || 0),
          };
        },
        { score: 0, maximum: 0 },
      ),
    [evaluationDetails],
  );

  const handleClose = () => {
    if (!isDownloading) onClose();
  };

  const handleRetry = () => {
    if (evaluators.length === 0) {
      setListRetryKey((current) => current + 1);
      return;
    }
    setDetailsRetryKey((current) => current + 1);
  };

  const handleEvaluatorChange = (event, evaluator) => {
    if (
      !evaluator ||
      String(evaluator.user_id) === String(selectedEvaluator?.user_id)
    ) {
      return;
    }
    setStatus(REQUEST_STATUS.LOADING);
    setEvaluationDetails([]);
    setErrorMessage("");
    setSelectedEvaluator(evaluator);
  };

  const handleDownload = async () => {
    if (
      status !== REQUEST_STATUS.SUCCESS ||
      !selectedEvaluator?.user_id ||
      isDownloading
    ) {
      return;
    }

    setIsDownloading(true);
    try {
      const candidateName = evaluationRow?.FULL_NAME || "Candidat";
      const seasonName = evaluationRow?.SAISON_NAME || "Saison";
      await evaluation_refonte_saison_service.downloadFicheEvaluation(
        candidateId,
        seasonId,
        null,
        selectedEvaluator.user_id,
        `Fiche evaluation - ${candidateName} - ${seasonName}.pdf`,
      );
    } catch (error) {
      showToast(
        "error",
        error?.message || "Impossible de télécharger la fiche d'évaluation.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const isSelectedAutoEvaluation = isAutoEvaluation(
    selectedEvaluator,
    candidateId,
  );
  const evaluationType = isSelectedAutoEvaluation
    ? "Auto-évaluation"
    : "Évaluation";
  const totalCompletion =
    totals.maximum > 0
      ? Math.min(
          100,
          Math.max(0, Math.round((totals.score / totals.maximum) * 100)),
        )
      : 0;
  const finalClassification = evaluationRow?.CLASSIFICATION || "Non classée";
  const finalClassificationColors =
    getClassificationColors(finalClassification);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: "85vw", md: "65vw", lg: "58vw" },
            maxWidth: 1100,
            boxShadow: "-10px 0 30px rgba(15, 23, 42, 0.14)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              flexShrink: 0,
              bgcolor: "#f8fafc",
              borderBottom: "1px solid",
              borderColor: "#e2e8f0",
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#0d47a1", fontWeight: 700 }}
                >
                  Aperçu de l'évaluation
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ mt: 0.25, color: "#334155", fontWeight: 700 }}
                >
                  {evaluationRow?.FULL_NAME || "Candidat non renseigné"}
                  {evaluationRow?.SAISON_NAME
                    ? ` — ${evaluationRow.SAISON_NAME}`
                    : ""}
                </Typography>
              </Box>
              <IconButton
                aria-label="Fermer l'aperçu"
                onClick={handleClose}
                disabled={isDownloading}
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <CloseOutlined />
              </IconButton>
            </Stack>

            {evaluators.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  borderColor: "#bbdefb",
                  boxShadow: "0 2px 8px rgba(13, 71, 161, 0.08)",
                }}
              >
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={evaluators}
                  value={selectedEvaluator}
                  disabled={isDownloading}
                  isOptionEqualToValue={(option, value) =>
                    String(option.user_id) === String(value.user_id)
                  }
                  getOptionLabel={(option) =>
                    getEvaluationOptionLabel(option, candidateId)
                  }
                  onChange={handleEvaluatorChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Évaluateur" />
                  )}
                />
              </Paper>
            )}
            {selectedEvaluator && (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Évaluateur : {getEvaluatorName(selectedEvaluator)} · Date :{" "}
                  {formatEvaluationDate(
                    selectedEvaluator.CREATION_DATE ||
                      evaluationRow?.DATE_EVALUATION,
                  )}
                </Typography>
                {isSelectedAutoEvaluation && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#0d47a1", fontWeight: 600 }}
                  >
                    Cette fiche correspond à l'auto-évaluation du candidat.
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
          {status === REQUEST_STATUS.LOADING && <LinearProgress />}
          <Divider />

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: { xs: 2, sm: 3 },
              bgcolor: "#f8fafc",
            }}
          >
            {status === REQUEST_STATUS.LOADING && (
              <Stack spacing={1.5} aria-label="Chargement de l'évaluation">
                <Paper
                  variant="outlined"
                  sx={{ height: 100, bgcolor: "grey.50" }}
                />
                <Paper
                  variant="outlined"
                  sx={{ height: 180, bgcolor: "grey.50" }}
                />
              </Stack>
            )}

            {status === REQUEST_STATUS.EMPTY && (
              <Alert severity="info">
                Aucune évaluation finalisée n'est disponible pour ce candidat et
                cette saison.
              </Alert>
            )}

            {status === REQUEST_STATUS.ERROR && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<RefreshOutlined />}
                    onClick={handleRetry}
                  >
                    Réessayer
                  </Button>
                }
              >
                {errorMessage || DEFAULT_ERROR_MESSAGE}
              </Alert>
            )}

            {status === REQUEST_STATUS.SUCCESS && (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ ...PREVIEW_CARD_SX, p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1.5 }}
                  >
                    Identification du poste
                  </Typography>
                  <Grid container spacing={2}>
                    <IdentificationItem
                      label="Session"
                      value={evaluationRow?.SAISON_NAME}
                    />
                    <IdentificationItem
                      label="Nom du titulaire"
                      value={evaluationRow?.FULL_NAME}
                    />
                    <IdentificationItem
                      label="Titre du poste"
                      value={evaluationRow?.function_name}
                    />
                    <IdentificationItem
                      label="Direction"
                      value={evaluationRow?.direction_code}
                    />
                    <IdentificationItem
                      label="Service"
                      value={evaluationRow?.service_code}
                    />
                    <IdentificationItem
                      label="Type d'évaluation"
                      value={evaluationType}
                    />
                  </Grid>
                </Paper>

                {categoryGroups.map((group) => {
                  const categoryTotals = group.details.reduce(
                    (result, detail) => {
                      const isNote = String(detail.TYPE_NAME || "")
                        .toLowerCase()
                        .includes("note");
                      if (!isNote) return result;
                      return {
                        score:
                          result.score +
                          Number(detail.NOTE ?? detail.NOTE_CA ?? 0),
                        maximum: result.maximum + Number(detail.NOTE_MAX || 0),
                      };
                    },
                    { score: 0, maximum: 0 },
                  );
                  const isScoredSection = [1, 2].includes(group.categoryOrder);
                  const isTextSection = [3, 4].includes(group.categoryOrder);

                  return (
                    <Paper
                      variant="outlined"
                      key={group.categoryName}
                      data-testid={`evaluation-section-${group.categoryOrder}`}
                      sx={{ ...PREVIEW_CARD_SX, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          bgcolor: "#e3f2fd",
                          borderLeft: "4px solid",
                          borderLeftColor: "#1976d2",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "#0d47a1", fontWeight: 700 }}
                        >
                          {group.categoryName}
                        </Typography>
                      </Box>
                      <Divider />
                      {group.details.map((detail, index) => {
                        const isNote = String(detail.TYPE_NAME || "")
                          .toLowerCase()
                          .includes("note");
                        const score = Number(
                          detail.NOTE ?? detail.NOTE_CA ?? 0,
                        );
                        const maximum = Number(detail.NOTE_MAX || 0);
                        const classification = getNoteClassification(
                          score,
                          maximum,
                        );
                        const classificationColors =
                          getClassificationColors(classification);
                        const comment =
                          detail.COMMENTAIRE_SUP ?? detail.COMM_CA ?? "";

                        return (
                          <Box
                            key={detail.ID_QUESTIONNAIRE || index}
                            sx={{
                              p: 2,
                              bgcolor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                              borderBottom: "1px solid",
                              borderColor: "#e2e8f0",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#1e293b", fontWeight: 600 }}
                            >
                              {index + 1}. {detail.QUESTION}
                            </Typography>
                            {!isTextSection && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Type de réponse :{" "}
                                {detail.TYPE_NAME || "Non renseigné"}
                              </Typography>
                            )}
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ sm: "center" }}
                              sx={{ mt: 1 }}
                            >
                              {isTextSection ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  maxRows={8}
                                  label="Réponse"
                                  value={getDisplayResponse(detail)}
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      bgcolor: "#ffffff",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#cbd5e1",
                                    },
                                  }}
                                />
                              ) : isNote ? (
                                <>
                                  <Typography variant="body2">
                                    Note donnée :{" "}
                                    <strong>
                                      {score} / {maximum}
                                    </strong>
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={classification || "Non classée"}
                                    sx={{
                                      color: classificationColors.color,
                                      bgcolor: classificationColors.background,
                                      border: `1px solid ${classificationColors.border}`,
                                      fontWeight: 600,
                                    }}
                                  />
                                </>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "pre-wrap" }}
                                >
                                  Réponse : {getDisplayResponse(detail)}
                                </Typography>
                              )}
                            </Stack>
                            {isNote && comment && (
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  whiteSpace: "pre-wrap",
                                  color: "text.secondary",
                                }}
                              >
                                Commentaire : {comment}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                      {isScoredSection && (
                        <Box
                          sx={{
                            px: 2,
                            py: 1.25,
                            bgcolor: "#eef6ff",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "#0d47a1", fontWeight: 700 }}
                          >
                            Total de la section : {categoryTotals.score} /{" "}
                            {categoryTotals.maximum}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "#0d47a1", fontWeight: 700 }}
                  >
                    Classification finale
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          height: "100%",
                          minHeight: 130,
                          p: 2.5,
                          borderRadius: 2,
                          borderColor: "#dbe4ee",
                          borderLeft: "3px solid #00a452",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ height: "100%" }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                              }}
                            >
                              NOTE GLOBALE
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                mt: 0.5,
                                color: "#0f172a",
                                fontWeight: 700,
                              }}
                            >
                              {totals.score}
                              <Typography
                                component="span"
                                variant="h6"
                                sx={{ color: "#475569", fontWeight: 400 }}
                              >
                                {" "}
                                / {totals.maximum}
                              </Typography>
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                              flexShrink: 0,
                            }}
                          >
                            <CircularProgress
                              variant="determinate"
                              value={100}
                              size={54}
                              thickness={3.5}
                              sx={{ color: "#e2e8f0" }}
                            />
                            <CircularProgress
                              variant="determinate"
                              value={totalCompletion}
                              size={54}
                              thickness={3.5}
                              sx={{
                                position: "absolute",
                                left: 0,
                                color: "#1976d2",
                                "& .MuiCircularProgress-circle": {
                                  strokeLinecap: "round",
                                },
                              }}
                            />
                            <Box
                              sx={{
                                inset: 0,
                                position: "absolute",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#0f172a", fontWeight: 700 }}
                              >
                                {totalCompletion}%
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          height: "100%",
                          minHeight: 130,
                          p: 2.5,
                          borderRadius: 2,
                          borderColor: finalClassificationColors.border,
                          borderLeft: `3px solid ${finalClassificationColors.color}`,
                          bgcolor: finalClassificationColors.background,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ height: "100%" }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: finalClassificationColors.color,
                                fontWeight: 700,
                                letterSpacing: 0.5,
                              }}
                            >
                              OBSERVATION
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                mt: 0.5,
                                color: finalClassificationColors.color,
                                fontWeight: 700,
                              }}
                            >
                              {finalClassification}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              color: finalClassificationColors.color,
                              bgcolor: "#ffffff80",
                            }}
                          >
                            <EmojiEventsOutlined fontSize="medium" />
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>

                <Paper variant="outlined" sx={{ ...PREVIEW_CARD_SX, p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Validation
                  </Typography>
                  <Typography variant="body2">
                    Candidat : {evaluationRow?.FULL_NAME || "Non renseigné"}
                  </Typography>
                  <Typography variant="body2">
                    Évaluateur : {getEvaluatorName(selectedEvaluator)}
                  </Typography>
                  <Typography variant="body2">
                    Date :{" "}
                    {formatEvaluationDate(
                      selectedEvaluator?.CREATION_DATE ||
                        evaluationRow?.DATE_EVALUATION,
                    )}
                  </Typography>
                </Paper>
              </Stack>
            )}
          </Box>

          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1.5}
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              flexShrink: 0,
              bgcolor: "background.paper",
            }}
          >
            <Button onClick={handleClose} disabled={isDownloading}>
              Fermer
            </Button>
            <LoadingButton
              variant="contained"
              startIcon={<DownloadOutlined />}
              loading={isDownloading}
              disabled={status !== REQUEST_STATUS.SUCCESS || !selectedEvaluator}
              onClick={handleDownload}
            >
              Télécharger
            </LoadingButton>
          </Stack>
        </Box>
      </Drawer>
      <Toast />
    </>
  );
};

export default EvaluationPreviewDrawer;
